import { useEffect } from "react";

// Canonical production origin — canonical/OG URLs always point here, even on preview,
// so search engines consolidate ranking signals on the live domain.
export const SITE_ORIGIN = "https://cardark.hiddenforge.co.uk";

function upsertMeta(attr, key, content) {
  if (content == null) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, prev, created };
}

export default function Seo({
  title,
  description,
  path = "",
  image = "/og-cover.png",
  type = "website",
  jsonLd,
}) {
  const ldStr = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    const url = SITE_ORIGIN + path;
    const absImage = image?.startsWith("http") ? image : SITE_ORIGIN + image;

    const restores = [];
    const track = (r) => r && restores.push(r);
    track(upsertMeta("name", "description", description));
    track(upsertMeta("property", "og:title", title));
    track(upsertMeta("property", "og:description", description));
    track(upsertMeta("property", "og:url", url));
    track(upsertMeta("property", "og:image", absImage));
    track(upsertMeta("property", "og:type", type));
    track(upsertMeta("name", "twitter:title", title));
    track(upsertMeta("name", "twitter:description", description));
    track(upsertMeta("name", "twitter:image", absImage));

    let canon = document.head.querySelector('link[rel="canonical"]');
    const canonCreated = !canon;
    const canonPrev = canon?.getAttribute("href");
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", url);

    let ld = null;
    if (ldStr) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.setAttribute("data-seo-jsonld", "true");
      ld.textContent = ldStr;
      document.head.appendChild(ld);
    }

    return () => {
      document.title = prevTitle;
      restores.forEach(({ el, prev, created }) => {
        if (created) el.remove();
        else if (prev != null) el.setAttribute("content", prev);
      });
      if (canonCreated) canon.remove();
      else if (canonPrev != null) canon.setAttribute("href", canonPrev);
      if (ld) ld.remove();
    };
  }, [title, description, path, image, type, ldStr]);

  return null;
}
